import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { AngularFireFunctions } from "@angular/fire/compat/functions";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { defaultIfEmpty, filter, take, takeUntil } from "rxjs/operators";
import { firstValueFrom, Subject, Subscription } from "rxjs";
import { SyndicateTicketsComponent } from "src/app/pages/syndicate/syndicate-tickets/syndicate-tickets.component";
import { ModalController, IonicModule } from "@ionic/angular";
import { PaymentService } from "src/app/services/payment.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { MySnackBarService } from "@core/my-snack-bar.service";
@Component({
    selector: "app-successful-payment",
    templateUrl: "./successful-payment.component.html",
    styleUrls: ["./successful-payment.component.scss"],
    standalone: true,
    imports: [IonicModule, TranslatePipe],
})
export class SuccessfulPaymentComponent implements OnInit, OnDestroy {
  isRedirected: boolean = false;
  translatedToastMsg: any;
  selectedLanguage: string = "en";
  /** 15s is a fallback threshold only — ticket retrieval starts immediately. */
  private flowCompleted = false;
  private ticketUiOpened = false;
  /** Blocks overlapping attemptContinue / goToSuccessfulPaymentPage while async work is in flight. */
  private isContinuing = false;
  private ticketReadySub: Subscription | null = null;
  private deadlineSub: Subscription | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private authService: AuthService,
    public translate: TranslateService,
    public angularFireFunctions: AngularFireFunctions,
    public angularFireStore: AngularFirestore,
    private modalController: ModalController,
    private paymentService: PaymentService,
    private GTMSegment: GTMSegmentService,
    private snackService: MySnackBarService
  ) {
    // Hope Meta Pixel + CAPI on confirmed payment (4793 AddPaymentInfo / 4794 Purchase).
    // Generic GTMInitilization remains commented — Hope events are scoped separately.
    const hopeProsubId =
      this.paymentService?.userProduct?.prosub_id ||
      this.GTMSegment.userProduct$?.value?.prosub_id ||
      localStorage.getItem("prosub_id");
    this.GTMSegment.trackHopePageView(hopeProsubId);
    this.GTMSegment.trackHopePaymentConfirmed(hopeProsubId);
    this.paymentService.userProduct = this.GTMSegment.userProduct$?.value;
    this.authService?.selectedlanguage$?.subscribe(
      (l) => {
        this.selectedLanguage = l;
        translate.use(l ? l : "en");
      },
      (err) => {
        return err;
      }
    );

    this.authService?.getConstantsMsg$?.subscribe((msg: any) => {
      this.translatedToastMsg = msg?.static_messages_pwa;
    });
  }

  ngOnInit() {
    this.isRedirected = false;
    this.startPostPaymentSuccessFlow();
  }

  ngOnDestroy() {
    this.ticketReadySub?.unsubscribe();
    this.deadlineSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Attempt ticket / post-payment continuation immediately.
   * Shared 15s deadline starts when the ticket UI opens (not after id resolution).
   */
  private startPostPaymentSuccessFlow(): void {
    this.attemptContinue();

    this.ticketReadySub = this.paymentService.salesJournyPaymentDetails$
      .pipe(
        filter((id) => !!id && id !== "0"),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.flowCompleted) {
          this.attemptContinue();
        }
      });
  }

  private bindSharedTicketDeadlineFallback(): void {
    this.deadlineSub?.unsubscribe();
    this.deadlineSub = this.paymentService
      .postPaymentDeadline$()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.flowCompleted && !this.paymentService.arePostPaymentTicketsVisible()) {
          void this.redirectToSyndicateGenerating();
        }
      });
  }

  private markFlowCompleted(): void {
    this.flowCompleted = true;
    this.isContinuing = false;
    this.ticketReadySub?.unsubscribe();
    this.ticketReadySub = null;
    this.deadlineSub?.unsubscribe();
    this.deadlineSub = null;
  }

  private attemptContinue(): void {
    if (this.flowCompleted || this.ticketUiOpened || this.isContinuing) {
      return;
    }

    if (this.paymentService?.freeRegJourneySuccessURL != "") {
      if (this.paymentService?.freeRegJourneyStep == "free-reg-journey-4d") {
        this.markFlowCompleted();
        this.router.navigateByUrl(`${this.paymentService?.freeRegJourneySuccessURL}`);
        return;
      }
      if (this.paymentService?.freeRegJourneyStep == "free-reg-journey-4g") {
        this.markFlowCompleted();
        this.router.navigateByUrl(`${this.paymentService?.freeRegJourneySuccessURL}`);
        return;
      }
    }

    void this.goToSuccessfulPaymentPage();
  }

  /** Fallback when tickets are not ready within the allowed window. */
  private async redirectToSyndicateGenerating(): Promise<void> {
    this.markFlowCompleted();
    try {
      await firstValueFrom(
        this.paymentService.setSyndicatePending().pipe(defaultIfEmpty(null), take(1))
      );
    } catch (error) {
      this.authService.errorHandlerLogs("setSyndicatePending on fallback:", error);
    }
    try {
      await this.modalController.dismiss();
    } catch {
      // Overlay may already be closed.
    }
    await this.router.navigateByUrl("mainTabs/tabs/syndicate");
  }

  /**
   * Always open the sales-journey modal. An existing syndicate ID must not take the
   * one-shot getSyndicateInfoWithProducts path (empty products could mark tickets visible
   * and bypass waitForTicketProducts + the shared 15s deadline).
   */
  fetchSyndicateAndShowTicket() {
    const existingId = this.paymentService?.salesJournyPaymentDetails$?.value;
    if (existingId && existingId !== "0") {
      this.paymentService.salesJournyPaymentDetails$.next(existingId);
    }
    void this.showTicketAsSalesJourney();
  }

  /** Sales-journey modal: absolute 15s deadline starts when the ticket UI opens. */
  async showTicketAsSalesJourney() {
    this.ticketUiOpened = true;
    this.ticketReadySub?.unsubscribe();
    this.ticketReadySub = null;
    this.paymentService.startPostPaymentTicketDeadline();
    this.bindSharedTicketDeadlineFallback();
    try {
      const syndicateTickets = await this.modalController.create({
        component: SyndicateTicketsComponent,
        componentProps: {
          syndicate: { syndicate_id: null, product: [] },
          isSalesJourney: true,
          isAddCardCashPurchase: localStorage.getItem("sjFlow") === "addCard",
        },
        cssClass: "modal-fullScreen",
      });

      syndicateTickets.onDidDismiss().then(() => {
        if (!this.paymentService.arePostPaymentTicketsVisible()) {
          // Non-terminal dismiss must not permanently block attemptContinue / goToSuccessfulPaymentPage.
          this.ticketUiOpened = false;
          this.isContinuing = false;
          if (!this.flowCompleted) {
            void this.redirectToSyndicateGenerating();
          }
          return;
        }
        this.markFlowCompleted();
        setTimeout(() => {
          this.router.navigateByUrl("/mainTabs/tabs/home");
        }, 1000);
      }).catch((err) => {
        this.authService.errorHandlerLogs("syndicateTickets onDidDismiss", err);
        this.ticketUiOpened = false;
        this.isContinuing = false;
      });

      return await syndicateTickets.present();
    } catch (error) {
      this.authService.errorHandlerLogs("showTicketAsSalesJourney modal failed", error);
      this.ticketUiOpened = false;
      this.isContinuing = false;
      if (!this.flowCompleted) {
        void this.redirectToSyndicateGenerating();
      }
    }
  }

  call4dFlow() {
    this.markFlowCompleted();
    this.router.navigateByUrl("/voucherRedeemed");
    localStorage.removeItem("4dpage");
    localStorage.removeItem("4cpage");
  }

  async goToSuccessfulPaymentPage() {
    if (this.flowCompleted || this.ticketUiOpened || this.isContinuing) {
      return;
    }
    this.isContinuing = true;
    // need to amend the INT2 code
    await this.paymentService
      .getOptionalPage("")
      .then(async (resp) => {
        if (this.flowCompleted || this.ticketUiOpened) {
          this.isContinuing = false;
          return;
        }
        const post_name = resp;
        if (post_name === "free-reg-complete-profile") {
          try {
            this.authService?.userDetails$.pipe(take(1), takeUntil(this.destroy$))?.subscribe(
              (user: any) => {
                if (this.flowCompleted || this.ticketUiOpened) {
                  this.isContinuing = false;
                  return;
                }
                if (user?.minimal_required == true) {
                  let obj = localStorage.getItem("4dpage");
                  let obj2 = localStorage.getItem("4cpage");
                  const freeRegJourney = localStorage.getItem("free_reg_journey");
                  if (freeRegJourney == "free-reg-step4g") {
                    this.markFlowCompleted();
                    const post_name = "mainTabs/tabs/syndicate?trigger=activated";
                    this.router.navigateByUrl(`${post_name}`);
                  } else if (obj == "free-reg-step-4d" || obj2 == "free-reg-step-4c") {
                    this.call4dFlow();
                  } else {
                    this.fetchSyndicateAndShowTicket();
                  }
                } else {
                  this.markFlowCompleted();
                  this.router.navigateByUrl(post_name);
                }
              },
              (err) => {
                this.isContinuing = false;
                /* const msg = this.translatedToastMsg?.["1013"]?.[this.selectedLanguage];
                this.authService.showToast(msg ? msg : "Something went wrong (user)", "Error", ""); */
                this.authService.getConstantsMsg("1013").then((translatedMsg) => {
                    const defaultMsg = {
                      message: {
                        line_1: "Something went wrong (user)",
                        line_2: "",
                      },
                      style: {
                        icon: "tick circle error.svg",
                        type: "Error",
                      },
                    }
                    this.authService.showToastMessage(translatedMsg, defaultMsg);
                  })
                  .catch((error) => {
                    return error;
                  });
              }
            );
          } catch (error) {
            this.isContinuing = false;
            /*  const msg = this.translatedToastMsg?.["1014"]?.[this.selectedLanguage];
             this.authService.showToast(msg ? msg : "Something went wrong", "Error", ""); */

            this.authService.getConstantsMsg("1014").then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  }
              }
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((error) => {
                return error;
              });
          }
        } else {
          this.markFlowCompleted();
          this.router.navigateByUrl(post_name);
        }
      })
      .catch(async (error) => {
        this.isContinuing = false;
        //this.authService.showToast(error ? error : "Something went wrong!", "Error", "");
        const translatedMsg = {
          message: {
            line_1: error,
            line_2: "",
          },
          style: {
            duration: 2000,
            icon: "tick circle error.svg",
            type: "Error",
          }
        }
        const defaultMsg = {
          message: {
            line_1: "Something went wrong!",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        }
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      });
  }

  /** CTA must progress the post-payment flow, not only toggle spinner state. */
  startSpinner() {
    this.isRedirected = true;
    this.attemptContinue();
  }
}
